/**
 * Layouts API: TanStack Query hooks wrapping Supabase CRUD for the
 * `layouts` table. One layout per wedding (1:1) — see schema.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import type { Layout, LayoutObject } from '../domain/types';
import type { Database, LayoutObjectJson } from '../lib/database.types';

type LayoutRow = Database['public']['Tables']['layouts']['Row'];

function rowToLayout(row: LayoutRow): Layout {
  return {
    weddingId: row.wedding_id,
    objects: row.objects as unknown as LayoutObject[],
    generatedAt: row.generated_at ?? '',
    lastEditedAt: row.last_edited_at,
    // Added by migration 0015. DB defaults to true, so existing layouts
    // keep showing warnings unless a coordinator explicitly turns them off.
    showWarningsOnSetupSheet: row.show_warnings_on_setup_sheet,
  };
}

/** One query fetching every wedding_id that has a layout row, rather than
 * one existence-check query per wedding — this scales fine as the venue
 * accumulates hundreds of past weddings, where an N+1 pattern would not.
 * Shared between TemplateLibraryPage (only weddings with a layout can be
 * used as a template) and DashboardPage (only weddings with a layout can
 * be safely duplicated — duplicating a layout-less wedding would fail). */
export function useWeddingIdsWithLayouts() {
  return useQuery({
    queryKey: ['wedding-ids-with-layouts'],
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await supabase.from('layouts').select('wedding_id');
      if (error) throw error;
      return new Set(data.map((row) => row.wedding_id));
    },
    staleTime: 60 * 1000,
  });
}

const LAYOUT_KEY = ['layout'] as const;

export function useLayout(weddingId: string | undefined) {
  return useQuery({
    queryKey: [...LAYOUT_KEY, weddingId],
    queryFn: async (): Promise<Layout | null> => {
      const { data, error } = await supabase
        .from('layouts')
        .select('*')
        .eq('wedding_id', weddingId!)
        .maybeSingle();

      if (error) throw error;
      return data ? rowToLayout(data) : null;
    },
    enabled: !!weddingId,
  });
}

export interface CreateLayoutInput {
  weddingId: string;
  objects: LayoutObject[];
  generatedAt: string;
}

/**
 * Creates the initial layout row immediately after auto-generation
 * (PRD §4.1 step 7-9). Called exactly once per wedding under normal use;
 * if a layout already exists for this wedding, the unique constraint on
 * wedding_id will reject a second insert — callers should check
 * useLayout() first and route to an update/regenerate flow instead.
 */
export function useCreateLayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateLayoutInput): Promise<Layout> => {
      const { data, error } = await supabase
        .from('layouts')
        .insert({
          wedding_id: input.weddingId,
          objects: input.objects as unknown as LayoutObjectJson[],
          generated_at: input.generatedAt,
        })
        .select()
        .single();

      if (error) throw error;
      return rowToLayout(data);
    },
    onSuccess: (layout) => {
      queryClient.invalidateQueries({ queryKey: [...LAYOUT_KEY, layout.weddingId] });
    },
  });
}

export interface SaveLayoutInput {
  weddingId: string;
  objects: LayoutObject[];
  showWarningsOnSetupSheet?: boolean;
}

/**
 * Saves (overwrites) the current object graph for a wedding's layout.
 * Used by both the explicit "Save" toolbar action and the debounced
 * autosave hook (PRD §11.1) — both paths call this same function, so
 * there is exactly one write path to keep in sync, not two.
 */
export function useSaveLayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SaveLayoutInput): Promise<Layout> => {
      const { data, error } = await supabase
        .from('layouts')
        .update({
          objects: input.objects as unknown as LayoutObjectJson[],
          last_edited_at: new Date().toISOString(),
          ...(input.showWarningsOnSetupSheet !== undefined && {
            show_warnings_on_setup_sheet: input.showWarningsOnSetupSheet,
          }),
        })
        .eq('wedding_id', input.weddingId)
        .select()
        .single();

      if (error) throw error;
      return rowToLayout(data);
    },
    onSuccess: (layout) => {
      queryClient.invalidateQueries({ queryKey: [...LAYOUT_KEY, layout.weddingId] });
    },
  });
}

/**
 * Duplicates a source wedding's layout object graph onto a target wedding
 * (PRD §12.2). A literal copy — does NOT re-run the generation engine,
 * since the coordinator explicitly chose a known-good starting point.
 * Object ids are NOT regenerated; PRD §12.2 says labels/structure are
 * preserved as-is on duplication.
 */
export function useDuplicateLayout() {
  const queryClient = useQueryClient();
  const createLayout = useCreateLayout();

  return useMutation({
    mutationFn: async ({
      sourceWeddingId,
      targetWeddingId,
    }: {
      sourceWeddingId: string;
      targetWeddingId: string;
    }): Promise<Layout> => {
      const { data: sourceData, error: sourceError } = await supabase
        .from('layouts')
        .select('*')
        .eq('wedding_id', sourceWeddingId)
        .single();

      if (sourceError) throw sourceError;

      return createLayout.mutateAsync({
        weddingId: targetWeddingId,
        objects: sourceData.objects as unknown as LayoutObject[],
        generatedAt: sourceData.generated_at ?? new Date().toISOString(),
      });
    },
    onSuccess: (layout) => {
      queryClient.invalidateQueries({ queryKey: [...LAYOUT_KEY, layout.weddingId] });
    },
  });
}
